import React from 'react';
import { Card, CardBody, Typography } from '@one-thd/sui-atomic-components';
import { useConfigService } from '@thd-nucleus/experience-context';
import { usePayments } from '../../../hooks/use-payments';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { useModal } from '../../../hooks/use-modal';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import {
  IMenuItem,
  MeatballsMenu,
} from '../../shared/meatballs-menu/meatballs-menu';
import { PHAlogo } from '../../shared/images/PHAlogo';
import { T2CPendingAlert } from '../t2c-pending-alert/t2c-pending-alert';
import { PHABadge } from '../pro-house-account-badge/PHA-badge';
import {
  IResendInviteModalProps,
  ResendInviteModal,
} from '../resend-invite-modal/resend-invite-modal';
import { ERR_UNABLE_TO_RESEND_INVITE } from '../../../constants/response-error-messages';
import { T2CMultiPendingAlert } from '../t2c-multi-pending-alert/t2c-multi-pending-alert';
import {
  ResendMultiInviteModal,
  IResendMultiInviteModalProps,
} from '../resend-multi-invite-modal/resend-multi-invite-modal';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { getMultiApproverValues } from '../../../utils/multi-approvers';
import { getHyphenatedPhoneNumber } from '../../../utils/general';
import { IT2CMultiResendInviteRequest } from '../../../models/b2b/payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { setResendPendingInvitesAnalytics } from '../../../utils/multi-approvers-analytics';

interface IProHouseCardProps {
  card: IPaymentCardCommonData;
  shouldDisableProjectLOC: boolean | null;
  onActionSuccess: (paymentId: string, message: string) => void;
  onActionFailure: (paymentId: string, message: string) => void;
  isAdmin: boolean;
  hasPTCPermissions: boolean;
}

export const LineOfCredit: React.FC<IProHouseCardProps> = ({
  shouldDisableProjectLOC,
  card,
  onActionFailure,
  onActionSuccess,
  isAdmin,
  hasPTCPermissions,
}): JSX.Element => {
  const { enrollPHAT2C, t2cMultipleApproverContacts } = usePayments();
  const { openModal, setComponentProps, hideModal } = useModal();
  const {
    locName,
    cardNumberLast4,
    locType,
    paymentId,
    t2cPrimary,
    t2cNumber,
  } = card;
  const {
    onProHouseAccountRemotePurchase,
    onProHouseAccountSelectPermissions,
  } = useMeatballMenuActions();
  const { svocID } = useCustomerInfo();

  const multipleApproversAllowedSvocIDs = useConfigService(
    'T2C_MULTIPLE_APPROVERS_ALLOWED_SVOCIDS',
  );
  const isSvocIDAllowedForMultipleAprovers =
    typeof multipleApproversAllowedSvocIDs === 'string' &&
    multipleApproversAllowedSvocIDs.includes(svocID);

  const multipleApproversEnabled =
    !!useConfigService('T2C_MULTIPLE_APPROVERS_ENABLED') ||
    isSvocIDAllowedForMultipleAprovers;

  const {
    pendingT2CAuthPhoneNumbers,
    numberOfPendingT2CAuthContacts,
    numberOfT2CAuthContacts,
    showT2CMultiPendingAlert,
    isT2CAuthorized,
    isT2CPending,
  } = getMultiApproverValues(
    t2cMultipleApproverContacts,
    multipleApproversEnabled,
    card.t2cStatus,
  );

  const isInactive = card.cardStatus?.toLowerCase() === 'inactive';
  const supportPhone = '1-888-475-8360';
  const lineLabelMap: Record<string, string> = {
    PROJECT: 'project line',
    SHOP: 'shop line',
  };
  const inactiveLineLabel = lineLabelMap[locType || ''] || 'line';
  const inactiveMessage = `This ${inactiveLineLabel} is inactive. For questions or assistance, please call ${supportPhone}.`;
  const nameTextColor = isInactive ? 'sui-text-inactive' : 'sui-text-primary';
  const cardNumberColor = isInactive ? 'inactive' : 'subtle';

  const menuItems: IMenuItem[] = [
    {
      value: 'Remote Purchases',
      action: () => {
        onProHouseAccountRemotePurchase(card);
      },
      isHidden: false,
      isDisabled:
        (card.locType === 'PROJECT' && !!shouldDisableProjectLOC) || isInactive,
      dataTestId: 'remote-purchases',
    },
    {
      value: 'Edit User Permissions',
      action: () => {
        onProHouseAccountSelectPermissions(card);
      },
      isHidden: !isAdmin || !hasPTCPermissions,
      dataTestId: 'edit-user-permissions',
    },
  ];

  const onResendInvite = () => {
    if (!t2cNumber) return;
    setComponentProps({ isLoading: true });

    enrollPHAT2C({
      t2cNumber,
      paymentId,
      locType,
      isResend: true,
      t2cApprovers: null,
    })
      .then(() => {
        onActionSuccess(
          paymentId,
          `Text2Confirm invite resent to ${getHyphenatedPhoneNumber(
            t2cNumber,
          )}.`,
        );
      })
      .catch(() => {
        onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const openResendInviteModal = () => {
    if (!t2cNumber) return;

    const props: IResendInviteModalProps = {
      isLoading: false,
      t2cNumber,
      onResendInvite,
    };

    openModal(ResendInviteModal, props, { size: 'sm' });
  };

  const onResendMultiInvites = () => {
    if (!pendingT2CAuthPhoneNumbers.length) return;
    setComponentProps({ isLoading: true });
    const t2cResendInviteRequest: IT2CMultiResendInviteRequest = {
      t2cResendInviteRequest: {
        resendAllPending: true,
        resendInvites: null,
      },
    };

    b2bPaymentsApi
      .multiResendT2CRequest(t2cResendInviteRequest)
      .then(({ data: { success } }) => {
        if (!success) {
          onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
          return;
        }
        setResendPendingInvitesAnalytics(pendingT2CAuthPhoneNumbers.length);
        onActionSuccess(
          paymentId,
          `Text2Confirm invite resent to ${pendingT2CAuthPhoneNumbers
            .map((approverT2cNumber) =>
              getHyphenatedPhoneNumber(approverT2cNumber),
            )
            .join(', ')}.`,
        );
      })
      .catch(() => {
        onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const openResendMultiInvitesModal = () => {
    if (!pendingT2CAuthPhoneNumbers.length) return;

    const props: IResendMultiInviteModalProps = {
      isLoading: false,
      onResendMultiInvites,
      pendingT2CAuthPhoneNumbers,
    };

    openModal(ResendMultiInviteModal, props, { size: 'sm' });
  };

  return (
    <Card className="sui-h-full">
      <CardBody>
        <div
          data-testid="pro-house-account"
          className="sui-flex sui-flex-col sui-gap-3"
        >
          <div className="sui-flex sui-flex-col sui-gap-1">
            <div className="sui-flex sui-justify-between sui-mb-2">
              <div className="sui-flex sui-truncate sui-items-center">
                <h3
                  className={`sui-font-display sui-text-3xl sui-mr-4 sui-truncate ${nameTextColor}`}
                >
                  {locName}
                </h3>
                {locType && <PHABadge locType={locType} />}
              </div>
              {!isInactive && (
                <div className="sui-ml-4">
                  <MeatballsMenu label="Line of Credit" items={menuItems} />
                </div>
              )}
            </div>
            <div className="sui-flex sui-flex-wrap sui-mb-4">
              <div style={isInactive ? { filter: 'grayscale(1)' } : undefined}>
                <PHAlogo />
              </div>

              <div className="sui-ml-4">
                <Typography variant="body-lg" color={cardNumberColor}>
                  ****{cardNumberLast4}
                </Typography>
              </div>
            </div>
            {!isInactive && isT2CAuthorized && (
              <Typography color="primary">
                Text2Confirm Authorized
                {t2cPrimary && (
                  <span className="sui-text-primary"> - Primary</span>
                )}
              </Typography>
            )}
            {!isInactive &&
              !showT2CMultiPendingAlert &&
              isT2CPending &&
              t2cNumber && (
                <T2CPendingAlert
                  openResendInviteModal={openResendInviteModal}
                />
              )}
            {!isInactive && showT2CMultiPendingAlert && (
              <T2CMultiPendingAlert
                numberOfPendingT2CAuthContacts={numberOfPendingT2CAuthContacts}
                numberOfContacts={numberOfT2CAuthContacts}
                onClickResend={openResendMultiInvitesModal}
              />
            )}
            {isInactive && (
              <Typography variant="body-sm">{inactiveMessage}</Typography>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
